'use client'

/* ---------------------------------
DonateSection
--------------------------------- */

import { ProgressCardType } from '@/types'
import { trackCTAClick } from '@/utils/gtm'
import { useMediaQuery } from '@uidotdev/usehooks'
import classNames from 'classnames'
import {
  Anchor,
  Button,
  OpeningTitle,
  Paragraph,
  Section,
  Title,
} from 'components/atoms'
import { ProgressCard } from 'components/molecules'
import { useRouter } from 'next/navigation'
import PropTypes from 'prop-types'

export default function DonateSection({ data }) {
  const { attributes } = data ?? {}
  const {
    openingTitle,
    title,
    paragraph,
    ctaButton,
    progressCard,
    layout,
    bg: sectionBg,
    textAlign,
  } = attributes ?? {}

  const router = useRouter()

  const isLG = useMediaQuery('only screen and (min-width: 1024px)')

  return (
    <Section
      bg={sectionBg}
      pyXL
      className={classNames(
        'DonateSection',
        layout === 'invert_cols' && 'DonateSection--layout_invert_cols'
      )}
    >
      <div className='DonateSection__grid'>
        <div className='DonateSection__grid__col-1'>
          {openingTitle ? (
            <OpeningTitle
              negative
              taLeft={isLG || textAlign === 'left'}
              mbS
            >
              {openingTitle}
            </OpeningTitle>
          ) : null}

          {title ? (
            <Title
              negative
              taLeft={isLG || textAlign === 'left'}
              mt0
              mbS
              level={2}
            >
              {/* TODO sanitize */}
              <span dangerouslySetInnerHTML={{ __html: title }} />
            </Title>
          ) : null}

          {paragraph ? (
            <Paragraph
              negative
              mbS
              taLeft={isLG || textAlign === 'left'}
            >
              {paragraph}
            </Paragraph>
          ) : null}

          {ctaButton ? (
            isLG ? (
              <Button
                mtXXS
                className={'DonateSection__cta-button'}
                variant={'cta'}
                // TODO what if it's a fully qualified URL
                onClick={() => {
                  trackCTAClick(ctaButton.label)
                  router.push(ctaButton.url)
                }}
              >
                {ctaButton.label}
              </Button>
            ) : (
              <Anchor
                styleProps={{
                  dBlock: true,
                  mbL: true,
                  taCenter: !isLG && textAlign === 'center',
                }}
                href={ctaButton.url}
                onClick={() => {
                  trackCTAClick(ctaButton.label)
                }}
              >
                {ctaButton.label}
              </Anchor>
            )
          ) : null}
        </div>

        <div className='DonateSection__grid__col-2'>
          {progressCard ? (
            <ProgressCard
              figures={progressCard.figures}
              testimonials={progressCard.testimonials}
              ctaMessage={progressCard.ctaMessage}
            />
          ) : null}
        </div>
      </div>
    </Section>
  )
}

DonateSection.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.number,
    attributes: PropTypes.shape({
      // this would represent a CMS enumeration
      layout: PropTypes.oneOf(['invert_cols', 'single_col']),
      bg: PropTypes.oneOf([
        'dark',
        'black',
        'blue',
        'dark-blue',
        'dark-green',
        'green',
        'grey-050',
        'grey-100',
        'grey-200',
        'grey-300',
        'grey-500',
        'grey-700',
        'grey-900',
        'rich-blue',
        'white',
      ]),
      textAlign: PropTypes.oneOf(['left', 'center']),
      openingTitle: PropTypes.string,
      title: PropTypes.string,
      paragraph: PropTypes.string,
      ctaButton: PropTypes.shape({
        label: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
      progressCard: ProgressCardType,
      createdAt: PropTypes.string,
      updatedAt: PropTypes.string,
      publishedAt: PropTypes.string,
    }),
  }).isRequired,
}
